import * as THREE from 'three';
import { BrepCurve, BrepEdge, BrepVertex, BrepBody } from './FikoBrep';

/**
 * FikoBrepCurveBuilder - Line, Spline, Polyline BRep Entegrasyonu
 *
 * Bu sınıf çizgi sistemlerini BRep topology'sine uyumlu şekilde oluşturur:
 * - Line: İki nokta arası düz çizgi
 * - Arc: Yay segmenti (merkez, yarıçap, açılar)
 * - Circle: Tam daire
 * - Ellipse: Elips
 * - Spline: B-Spline veya NURBS eğrisi
 * - Bezier: Bezier eğrisi
 * - Polyline: Çoklu çizgi segmentleri
 *
 * Grup sistemi ile uyumlu: Her curve bir grup ID'si alır
 */
export class FikoBrepCurveBuilder {
    private tolerance: number;
    private nextVertexId: number = 0;
    private nextEdgeId: number = 0;

    constructor(tolerance: number = 1e-6) {
        this.tolerance = tolerance;
    }

    // ========== LINE ==========

    /**
     * İki nokta arası düz çizgi oluştur
     */
    public createLine(
        start: THREE.Vector3,
        end: THREE.Vector3,
        groupId?: number
    ): { vertices: BrepVertex[]; edge: BrepEdge } {
        const startVertex = this.createVertex(start);
        const endVertex = this.createVertex(end);

        const length = start.distanceTo(end);

        const curve: BrepCurve = {
            type: 'line',
            start: start.clone(),
            end: end.clone(),
            length
        };

        const edge = this.createEdge(startVertex.id, endVertex.id, curve, groupId);

        // Vertex'lere edge'i bağla
        startVertex.edges.push(edge.id);
        endVertex.edges.push(edge.id);

        return {
            vertices: [startVertex, endVertex],
            edge
        };
    }

    // ========== ARC ==========

    /**
     * Yay segmenti oluştur (merkez, yarıçap, başlangıç/bitiş açısı)
     */
    public createArc(
        center: THREE.Vector3,
        radius: number,
        startAngle: number,
        endAngle: number,
        normal: THREE.Vector3,
        groupId?: number
    ): { vertices: BrepVertex[]; edge: BrepEdge } {
        // Düzlem koordinat sistemi oluştur
        const uAxis = this.computePerpendicularAxis(normal);
        const vAxis = new THREE.Vector3().crossVectors(normal, uAxis).normalize();

        // Başlangıç ve bitiş noktaları
        const start = center.clone()
            .add(uAxis.clone().multiplyScalar(radius * Math.cos(startAngle)))
            .add(vAxis.clone().multiplyScalar(radius * Math.sin(startAngle)));

        const end = center.clone()
            .add(uAxis.clone().multiplyScalar(radius * Math.cos(endAngle)))
            .add(vAxis.clone().multiplyScalar(radius * Math.sin(endAngle)));

        const startVertex = this.createVertex(start);
        const endVertex = this.createVertex(end);

        // Yay uzunluğu
        let angleDiff = endAngle - startAngle;
        if (angleDiff < 0) angleDiff += 2 * Math.PI;
        const length = radius * angleDiff;

        const curve: BrepCurve = {
            type: 'arc',
            center: center.clone(),
            radius,
            startAngle,
            endAngle,
            normal: normal.clone(),
            length
        };

        const edge = this.createEdge(startVertex.id, endVertex.id, curve, groupId);

        startVertex.edges.push(edge.id);
        endVertex.edges.push(edge.id);

        return {
            vertices: [startVertex, endVertex],
            edge
        };
    }

    // ========== CIRCLE ==========

    /**
     * Tam daire oluştur (kapalı döngü)
     */
    public createCircle(
        center: THREE.Vector3,
        radius: number,
        normal: THREE.Vector3,
        groupId?: number
    ): { vertex: BrepVertex; edge: BrepEdge } {
        // Düzlem koordinat sistemi
        const uAxis = this.computePerpendicularAxis(normal);

        // Başlangıç noktası (0 derece)
        const start = center.clone().add(uAxis.clone().multiplyScalar(radius));
        const vertex = this.createVertex(start);

        const length = 2 * Math.PI * radius;

        const curve: BrepCurve = {
            type: 'circle',
            center: center.clone(),
            radius,
            normal: normal.clone(),
            length
        };

        // Kapalı döngü: start = end
        const edge = this.createEdge(vertex.id, vertex.id, curve, groupId);

        vertex.edges.push(edge.id);

        return { vertex, edge };
    }

    // ========== ELLIPSE ==========

    /**
     * Elips oluştur
     */
    public createEllipse(
        center: THREE.Vector3,
        majorAxis: THREE.Vector3, // Büyük eksen (uzunluk = semi-major)
        minorAxis: THREE.Vector3, // Küçük eksen (uzunluk = semi-minor)
        groupId?: number
    ): { vertex: BrepVertex; edge: BrepEdge } {
        // Başlangıç noktası (major axis üzerinde)
        const start = center.clone().add(majorAxis);
        const vertex = this.createVertex(start);

        // Elips çevresi (yaklaşık Ramanujan formülü)
        const a = majorAxis.length();
        const b = minorAxis.length();
        const length = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));

        const curve: BrepCurve = {
            type: 'ellipse',
            center: center.clone(),
            majorAxis: majorAxis.clone(),
            minorAxis: minorAxis.clone(),
            length
        };

        const edge = this.createEdge(vertex.id, vertex.id, curve, groupId);

        vertex.edges.push(edge.id);

        return { vertex, edge };
    }

    // ========== SPLINE (B-Spline / NURBS) ==========

    /**
     * B-Spline eğrisi oluştur
     */
    public createSpline(
        controlPoints: THREE.Vector3[],
        degree: number = 3,
        knots?: number[],
        groupId?: number
    ): { vertices: BrepVertex[]; edge: BrepEdge } {
        if (controlPoints.length < 2) {
            throw new Error('Spline en az 2 kontrol noktası gerektirir');
        }

        // Knot vektörü oluştur (uniform, eğer verilmemişse)
        const actualKnots = knots || this.generateUniformKnots(controlPoints.length, degree);

        // Başlangıç ve bitiş noktaları (spline değerlendirmesi)
        const start = this.evaluateSpline(controlPoints, actualKnots, degree, 0);
        const end = this.evaluateSpline(controlPoints, actualKnots, degree, 1);

        const startVertex = this.createVertex(start);
        const endVertex = this.createVertex(end);

        // Spline uzunluğu (numerik integrasyon)
        const length = this.calculateSplineLength(controlPoints, actualKnots, degree);

        const curve: BrepCurve = {
            type: 'spline',
            controlPoints: controlPoints.map(p => p.clone()),
            knots: actualKnots,
            degree,
            length
        };

        const edge = this.createEdge(startVertex.id, endVertex.id, curve, groupId);

        startVertex.edges.push(edge.id);
        endVertex.edges.push(edge.id);

        return {
            vertices: [startVertex, endVertex],
            edge
        };
    }

    /**
     * Uniform knot vektörü oluştur
     */
    private generateUniformKnots(n: number, degree: number): number[] {
        const knots: number[] = [];
        const m = n + degree + 1;

        for (let i = 0; i < m; i++) {
            if (i <= degree) {
                knots.push(0);
            } else if (i >= m - degree - 1) {
                knots.push(1);
            } else {
                knots.push((i - degree) / (n - degree));
            }
        }

        return knots;
    }

    /**
     * Spline değerlendirme (De Boor algoritması)
     */
    private evaluateSpline(
        controlPoints: THREE.Vector3[],
        knots: number[],
        degree: number,
        t: number
    ): THREE.Vector3 {
        const n = controlPoints.length - 1;

        // t'nin hangi knot aralığında olduğunu bul
        let k = degree;
        for (let i = degree; i < n + 1; i++) {
            if (t >= knots[i] && t < knots[i + 1]) {
                k = i;
                break;
            }
        }

        // t = 1 için son aralık
        if (t >= 1) {
            k = n;
        }

        // De Boor algoritması
        const d: THREE.Vector3[] = [];
        for (let j = 0; j <= degree; j++) {
            d.push(controlPoints[k - degree + j].clone());
        }

        for (let r = 1; r <= degree; r++) {
            for (let j = degree; j >= r; j--) {
                const alpha = (t - knots[k - degree + j]) / (knots[k + 1 + j - r] - knots[k - degree + j]);
                d[j].lerp(d[j - 1], 1 - alpha);
            }
        }

        return d[degree];
    }

    /**
     * Spline uzunluğu hesapla (numerik integrasyon)
     */
    private calculateSplineLength(
        controlPoints: THREE.Vector3[],
        knots: number[],
        degree: number,
        segments: number = 100
    ): number {
        let length = 0;
        let prevPoint = this.evaluateSpline(controlPoints, knots, degree, 0);

        for (let i = 1; i <= segments; i++) {
            const t = i / segments;
            const currentPoint = this.evaluateSpline(controlPoints, knots, degree, t);
            length += prevPoint.distanceTo(currentPoint);
            prevPoint = currentPoint;
        }

        return length;
    }

    // ========== BEZIER ==========

    /**
     * Bezier eğrisi oluştur
     */
    public createBezier(
        controlPoints: THREE.Vector3[],
        groupId?: number
    ): { vertices: BrepVertex[]; edge: BrepEdge } {
        if (controlPoints.length < 2) {
            throw new Error('Bezier en az 2 kontrol noktası gerektirir');
        }

        const start = controlPoints[0].clone();
        const end = controlPoints[controlPoints.length - 1].clone();

        const startVertex = this.createVertex(start);
        const endVertex = this.createVertex(end);

        // Bezier uzunluğu (numerik integrasyon)
        const length = this.calculateBezierLength(controlPoints);

        const curve: BrepCurve = {
            type: 'bezier',
            controlPoints: controlPoints.map(p => p.clone()),
            length
        };

        const edge = this.createEdge(startVertex.id, endVertex.id, curve, groupId);

        startVertex.edges.push(edge.id);
        endVertex.edges.push(edge.id);

        return {
            vertices: [startVertex, endVertex],
            edge
        };
    }

    /**
     * Bezier değerlendirme (De Casteljau algoritması)
     */
    public evaluateBezier(controlPoints: THREE.Vector3[], t: number): THREE.Vector3 {
        const n = controlPoints.length;
        const points = controlPoints.map(p => p.clone());

        for (let r = 1; r < n; r++) {
            for (let i = 0; i < n - r; i++) {
                points[i].lerp(points[i + 1], t);
            }
        }

        return points[0];
    }

    /**
     * Bezier uzunluğu hesapla
     */
    private calculateBezierLength(controlPoints: THREE.Vector3[], segments: number = 100): number {
        let length = 0;
        let prevPoint = controlPoints[0].clone();

        for (let i = 1; i <= segments; i++) {
            const t = i / segments;
            const currentPoint = this.evaluateBezier(controlPoints, t);
            length += prevPoint.distanceTo(currentPoint);
            prevPoint = currentPoint;
        }

        return length;
    }

    // ========== POLYLINE ==========

    /**
     * Polyline oluştur (birden fazla bağlı çizgi segmenti)
     */
    public createPolyline(
        points: THREE.Vector3[],
        closed: boolean = false,
        groupId?: number
    ): { vertices: BrepVertex[]; edges: BrepEdge[] } {
        if (points.length < 2) {
            throw new Error('Polyline en az 2 nokta gerektirir');
        }

        const vertices: BrepVertex[] = [];
        const edges: BrepEdge[] = [];

        // Vertex'ler oluştur
        points.forEach(p => {
            vertices.push(this.createVertex(p));
        });

        // Edge'ler oluştur
        for (let i = 0; i < points.length - 1; i++) {
            const line = this.createLineSegmentEdge(
                vertices[i],
                vertices[i + 1],
                groupId
            );
            edges.push(line);
        }

        // Kapalı polyline için son segment
        if (closed && points.length > 2) {
            const closingLine = this.createLineSegmentEdge(
                vertices[vertices.length - 1],
                vertices[0],
                groupId
            );
            edges.push(closingLine);
        }

        // Ayrıca bir polyline curve oluştur (tüm noktaları içeren)
        const totalLength = edges.reduce((sum, e) => sum + e.length, 0);

        // Master edge (polyline tipi) oluştur
        const polylineCurve: BrepCurve = {
            type: 'polyline',
            points: points.map(p => p.clone()),
            length: totalLength
        };

        const masterEdge: BrepEdge = {
            id: `E${this.nextEdgeId++}`,
            startVertex: vertices[0].id,
            endVertex: closed ? vertices[0].id : vertices[vertices.length - 1].id,
            curve: polylineCurve,
            faces: [],
            convex: true,
            angle: 0,
            length: totalLength
        };

        // groupId varsa userData olarak ekle
        if (groupId !== undefined) {
            (masterEdge as any).groupId = groupId;
        }

        return { vertices, edges: [...edges, masterEdge] };
    }

    /**
     * Line segment edge oluştur (polyline için yardımcı)
     */
    private createLineSegmentEdge(
        startVertex: BrepVertex,
        endVertex: BrepVertex,
        groupId?: number
    ): BrepEdge {
        const length = startVertex.position.distanceTo(endVertex.position);

        const curve: BrepCurve = {
            type: 'line',
            start: startVertex.position.clone(),
            end: endVertex.position.clone(),
            length
        };

        const edge: BrepEdge = {
            id: `E${this.nextEdgeId++}`,
            startVertex: startVertex.id,
            endVertex: endVertex.id,
            curve,
            faces: [],
            convex: true,
            angle: 0,
            length
        };

        if (groupId !== undefined) {
            (edge as any).groupId = groupId;
        }

        startVertex.edges.push(edge.id);
        endVertex.edges.push(edge.id);

        return edge;
    }

    // ========== BRep BODY'YE EKLEME ==========

    /**
     * Oluşturulan curve'ları BRep body'ye ekle
     */
    public addToBody(
        brep: BrepBody,
        vertices: BrepVertex[],
        edges: BrepEdge[]
    ): void {
        vertices.forEach(v => {
            brep.vertices.set(v.id, v);
        });

        edges.forEach(e => {
            brep.edges.set(e.id, e);
        });

        // Stats güncelle
        brep.stats.vertexCount = brep.vertices.size;
        brep.stats.edgeCount = brep.edges.size;
    }

    /**
     * Grup bazlı curve sorgusu
     */
    public getEdgesByGroup(brep: BrepBody, groupId: number): BrepEdge[] {
        const result: BrepEdge[] = [];

        brep.edges.forEach(edge => {
            if ((edge as any).groupId === groupId) {
                result.push(edge);
            }
        });

        return result;
    }

    // ========== YARDIMCI METODLAR ==========

    private createVertex(position: THREE.Vector3): BrepVertex {
        return {
            id: `V${this.nextVertexId++}`,
            position: position.clone(),
            tolerance: this.tolerance,
            edges: []
        };
    }

    private createEdge(
        startVertexId: string,
        endVertexId: string,
        curve: BrepCurve,
        groupId?: number
    ): BrepEdge {
        const edge: BrepEdge = {
            id: `E${this.nextEdgeId++}`,
            startVertex: startVertexId,
            endVertex: endVertexId,
            curve,
            faces: [],
            convex: true,
            angle: 0,
            length: curve.length
        };

        if (groupId !== undefined) {
            (edge as any).groupId = groupId;
        }

        return edge;
    }

    private computePerpendicularAxis(normal: THREE.Vector3): THREE.Vector3 {
        const arbitrary = Math.abs(normal.x) < 0.9
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 1, 0);

        return new THREE.Vector3().crossVectors(normal, arbitrary).normalize();
    }

    /**
     * ID sayaçlarını sıfırla (yeni BRep body için)
     */
    public resetCounters(): void {
        this.nextVertexId = 0;
        this.nextEdgeId = 0;
    }

    /**
     * ID sayaçlarını ayarla (mevcut BRep body'ye ekleme için)
     */
    public setCounters(vertexId: number, edgeId: number): void {
        this.nextVertexId = vertexId;
        this.nextEdgeId = edgeId;
    }
}

/**
 * Singleton curve builder instance
 */
let curveBuilderInstance: FikoBrepCurveBuilder | null = null;

export function getCurveBuilder(tolerance?: number): FikoBrepCurveBuilder {
    if (!curveBuilderInstance) {
        curveBuilderInstance = new FikoBrepCurveBuilder(tolerance);
    }
    return curveBuilderInstance;
}

export function disposeCurveBuilder(): void {
    curveBuilderInstance = null;
}
